import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { servicesContainer } from '@/core/di/services-container';
import { Language } from '../types/translation-types';

interface LanguageStore {
  // Data state
  languages: Language[];
  totalCount: number;

  // Loading states
  isLoading: boolean;

  // Error states
  error: string | null;

  // Metadata
  lastUpdated: Date | null;

  // Actions
  fetchLanguages: () => Promise<void>;
  getLanguageByCode: (code: string) => Language | undefined;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  languages: [],
  totalCount: 0,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

export const useLanguageStore = create<LanguageStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      fetchLanguages: async () => {
        const state = get();
        if (state.isLoading) {
          return;
        }

        // Skip if we already have languages loaded recently (within 5 minutes)
        if (state.languages.length > 0 && state.lastUpdated) {
          const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
          if (state.lastUpdated > fiveMinutesAgo) {
            return;
          }
        }

        set({ isLoading: true, error: null });

        try {
          const services = servicesContainer.getServices();
          const response = await services.getLanguagesUseCase.execute();
          const sortedLanguages = [...response.value].sort((a, b) => a.sortOrder - b.sortOrder);

          set({
            languages: sortedLanguages,
            totalCount: sortedLanguages.length,
            isLoading: false,
            lastUpdated: new Date(),
            error: null,
          });
        } catch (error) {
          console.error('LanguageStore.fetchLanguages: Failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load languages',
          });
        }
      },

      getLanguageByCode: (code: string) => {
        const state = get();
        return state.languages.find(lang => lang.languageCode === code);
      },

      clearError: () => set({ error: null }),

      reset: () => set(initialState),
    }),
    {
      name: 'language-storage',
      partialize: (state) => ({
        // Persist languages to avoid re-fetching on every page load
        languages: state.languages,
        lastUpdated: state.lastUpdated,
      }),
    }
  )
);
